import { z } from 'zod/v3';

// ========== Compliance Role DTOs ==========

export const ComplianceRoleHolderDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  employeeId: z.string(),
  userId: z.string().nullable(),
  name: z.string(),
  title: z.string().nullable(),
  email: z.string(),
  assignmentStatus: z.enum(['can_act', 'auto_upgraded', 'unlinked', 'blocked_persona']),
  workspaceRole: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type ComplianceRoleHolderDTO = z.infer<typeof ComplianceRoleHolderDTOSchema>;

export const ComplianceRoleDTOSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  /** Stable identity for a seeded canonical role; null for custom roles.
   *  Consumers resolve roles by this, not the user-editable `name`. */
  roleKey: z.string().nullable(),
  description: z.string().nullable(),
  holders: z.array(ComplianceRoleHolderDTOSchema),
  controlRefs: z.array(z.string()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ComplianceRoleDTO = z.infer<typeof ComplianceRoleDTOSchema>;

export const CreateComplianceRoleDTOSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  controlRefs: z.array(z.string()).default([]),
});
export type CreateComplianceRoleDTO = z.infer<typeof CreateComplianceRoleDTOSchema>;

export const UpdateComplianceRoleDTOSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional(),
  controlRefs: z.array(z.string()).optional(),
});
export type UpdateComplianceRoleDTO = z.infer<typeof UpdateComplianceRoleDTOSchema>;

// Canonical roles proposed by Vera based on team size and scope
export const CANONICAL_COMPLIANCE_ROLES = {
  // Core roles (always proposed)
  EXECUTIVE_SPONSOR: {
    name: 'Executive Sponsor',
    description: 'Signs off on policies, owns risk acceptance decisions, accountable to auditors for the compliance program.',
  },
  SECURITY_LEAD: {
    name: 'Security Lead',
    description: 'Owns access control reviews, incident response, security monitoring, and vulnerability management.',
  },
  CHANGE_MANAGER: {
    name: 'Change Manager',
    description: 'Approves production changes, reviews deployments, owns change control process.',
  },
  // Core: owns the PS (Personnel Security) and AT (Awareness & Training)
  // families by default — background checks, offboarding, training completion.
  // A two-person company still hires and offboards, so this is not a growth
  // role; the founder just holds the hat.
  HR_LEAD: {
    name: 'HR Lead',
    description: 'Manages employee onboarding/offboarding, background checks, training compliance.',
  },
  // Growth roles (proposed at 10+ people)
  INCIDENT_COMMANDER: {
    name: 'Incident Commander',
    description: 'Leads incident response, coordinates containment and recovery, manages post-incident reviews.',
  },
  PRIVACY_OFFICER: {
    name: 'Privacy Officer',
    description: 'Owns data protection policies, manages data subject requests, ensures privacy compliance.',
  },
  // Enterprise roles (proposed at 50+ people)
  COMPLIANCE_MANAGER: {
    name: 'Compliance Manager',
    description: 'Manages the compliance program, coordinates audits, tracks control effectiveness.',
  },
  RISK_OWNER: {
    name: 'Risk Owner',
    description: 'Owns the risk register, leads risk assessments, tracks risk remediation.',
  },
  VENDOR_MANAGER: {
    name: 'Vendor Manager',
    description: 'Manages vendor risk assessments, reviews third-party SOC reports, tracks vendor compliance.',
  },
  IT_OPERATIONS_LEAD: {
    name: 'IT Operations Lead',
    description: 'Owns infrastructure availability, backup/recovery, capacity planning, and system monitoring.',
  },
  // NOTE (RFC-194 / #1123): HIPAA does NOT get its own Security/Privacy
  // Officer roles. The 45 CFR §164.308(a)(2) named Security Official IS the
  // Security Lead (a core role that always exists for every framework), and
  // the §164.530(a)(1) Privacy Officer IS the generic Privacy Officer
  // (entity-gated to Covered Entities / Hybrids — a Business Associate has no
  // Privacy Rule duty). The HIPAA control citations are carried on those
  // roles' `controlRefs` by the HIPAA seeder, not via separate role rows.
} as const;

export type CanonicalRoleKey = keyof typeof CANONICAL_COMPLIANCE_ROLES;

/**
 * Maps a role's display `name` (including legacy / pre-#1123 names) to its
 * stable `CanonicalRoleKey`. Returns null for user-created custom roles —
 * those are never auto-referenced. This is the single name→key resolver used
 * by every creator and the backfill migration.
 */
const CANONICAL_ROLE_NAME_TO_KEY: Record<string, CanonicalRoleKey> = (() => {
  const map: Record<string, CanonicalRoleKey> = {};
  for (const key of Object.keys(CANONICAL_COMPLIANCE_ROLES) as CanonicalRoleKey[]) {
    map[CANONICAL_COMPLIANCE_ROLES[key].name] = key;
  }
  // Legacy names seeded before #1123 collapsed the HIPAA-prefixed roles.
  map['HIPAA Security Officer'] = 'SECURITY_LEAD';
  map['HIPAA Privacy Officer'] = 'PRIVACY_OFFICER';
  map['HIPAA Compliance Contact'] = 'COMPLIANCE_MANAGER';
  return map;
})();

export function canonicalRoleKeyForName(name: string): CanonicalRoleKey | null {
  return CANONICAL_ROLE_NAME_TO_KEY[name.trim()] ?? null;
}

export type ProposedRole = {
  key: CanonicalRoleKey;
  name: string;
  description: string;
};

/**
 * RFC-194: canonical roles that are REQUIRED and must not be deleted from the
 * role editor (a user may still rename the label or reassign the person — one
 * person can hold several). Only regulation-named / routing-critical roles
 * qualify:
 *   - SECURITY_LEAD — always: the SOC 2 security owner, the HIPAA §164.308(a)(2)
 *     Security Official, and the escalation/auto-assign routing target.
 *   - PRIVACY_OFFICER — HIPAA Covered Entity / Hybrid only (§164.530(a)(1));
 *     a Business Associate has no Privacy Rule duty. Unknown entity type is
 *     treated as Covered Entity (the seeder's default), so it stays required.
 */
export function requiredRoleKeys(
  frameworks: string[] = [],
  hipaaEntityType?: string | null,
): Set<CanonicalRoleKey> {
  const keys = new Set<CanonicalRoleKey>(['SECURITY_LEAD']);
  const hasHipaa = frameworks.some((f) => f.toUpperCase() === 'HIPAA');
  if (hasHipaa && hipaaEntityType !== 'BUSINESS_ASSOCIATE') {
    keys.add('PRIVACY_OFFICER');
  }
  return keys;
}

function roleEntry(key: CanonicalRoleKey): ProposedRole {
  return { key, ...CANONICAL_COMPLIANCE_ROLES[key] };
}

/**
 * Returns the canonical roles appropriate for the given team size, TSC
 * scope, and enrolled frameworks. HIPAA adds two required named roles
 * (Security Officer, Privacy Officer) regardless of team size.
 */
export function getProposedRoles(
  teamSize: number,
  trustServiceCriteria: string[] = ['Security'],
  frameworks: string[] = [],
): ProposedRole[] {
  // Security Lead is always present — it is also the HIPAA §164.308(a)(2)
  // named Security Official, so HIPAA needs no extra security role.
  //
  // HR Lead is core rather than 10+: `DEFAULT_OWNER_ROLE_BY_FAMILY` hands it
  // the PS and AT families unconditionally, so gating it on team size left a
  // small workspace with background-check, offboarding and security-training
  // work whose default owner the org had never been asked to name.
  const roles: ProposedRole[] = [
    roleEntry('EXECUTIVE_SPONSOR'),
    roleEntry('SECURITY_LEAD'),
    roleEntry('CHANGE_MANAGER'),
    roleEntry('HR_LEAD'),
  ];

  if (teamSize >= 10) {
    roles.push(roleEntry('INCIDENT_COMMANDER'));
  }

  // Privacy Officer if Confidentiality/Privacy in scope, large team, or
  // HIPAA (it is the §164.530(a)(1) Privacy Officer for Covered Entities;
  // entity-type gating for Business Associates happens at seed/scope time).
  const hasPrivacyScope = trustServiceCriteria.some(
    (c) => c === 'Confidentiality' || c === 'Privacy',
  );
  const hasHipaa = frameworks.some((f) => f.toUpperCase() === 'HIPAA');
  if (hasPrivacyScope || hasHipaa || teamSize >= 50) {
    roles.push(roleEntry('PRIVACY_OFFICER'));
  }

  if (teamSize >= 50) {
    roles.push(roleEntry('COMPLIANCE_MANAGER'));
    roles.push(roleEntry('RISK_OWNER'));
    roles.push(roleEntry('VENDOR_MANAGER'));
    roles.push(roleEntry('IT_OPERATIONS_LEAD'));
  }

  return roles;
}
